import React, { useCallback, useState, useRef, useEffect } from 'react';
import VideoService from '../../../../../../service/VideoService';
import { useLibraryContext } from '../../../../../../context/LibraryContext';
import useOutsideClick from '../../../../../../hooks/useOutsideClick';
import Toast from '../../../../../../../common/utils/Toast';
import CardsList from '../../../../../../components/CardList/CardList';
import ListHeader from '../../../ListHeader/ListHeader';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { approvalStatusesForReviewer } from '../../../VideosFilter/constants';
import { ToastNotification } from '../../../../../../components/Toast/Toast';

function ReviewCards(props) {
  const { searchInput, videos, listMode = false, loadVideos, loadVideosApproved = () => {}, selectedFilter } = props;
  const {
    selectedCard,
    toggleSelectedCard,
    toggleSelectedAllCard,
    toggleUnselectCards,
    isApproveVideo,
    toggleApproveVideo,
    isDeclineVideo,
    toggleDeclineVideo,
    isRevokeVideo,
    toggleRevokeVideo,
  } = useLibraryContext();

  const [showMenu, setShowMenu] = useState({});
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const menuRef = useRef(null);
  useOutsideClick(menuRef, () => {
    if (Object.keys(showMenu).length) {
      setShowMenu({});
    }
  });

  useEffect(() => {
    const approve = async () => {
      await approveVideos();
      toggleApproveVideo();
      toggleUnselectCards();
    };

    if (isApproveVideo) {
      approve();
    }
  }, [isApproveVideo]);

  useEffect(() => {
    const decline = async () => {
      await declineVideos();
      toggleDeclineVideo();
      toggleUnselectCards();
    };

    if (isDeclineVideo) {
      decline();
    }
  }, [isDeclineVideo]);

  useEffect(() => {
    const revoke = async () => {
      await revokeVideos();
      toggleRevokeVideo();
      toggleUnselectCards();
    };

    if (isRevokeVideo) {
      revoke();
    }
  }, [isRevokeVideo]);

  const approveVideos = useCallback(
    async (uuid, videoIndex, name) => {
      try {
        const uuids = Object.keys(selectedCard);

        if (uuid) {
          const loadingToast = ToastNotification({
            message: t('general.toast.approve_videos.approving'),
            spinner: true,
            isMobileView: isMobileView,
          });
          await VideoService.updateVideoReviewStatus(uuid, { approvalStatus: 4 });
          toast.dismiss(loadingToast);
          ToastNotification({
            message: `${name} ${t('general.toast.approve.success')}`,
            iconName: 'success',
            isMobileView: isMobileView,
          });
          loadVideos();
          loadVideosApproved();
          return;
        }

        if (uuids.length) {
          const loadingToast = ToastNotification({
            message: t('general.toast.approve_videos.approving'),
            spinner: true,
            isMobileView: isMobileView,
          });
          toggleUnselectCards();
          await Promise.all(uuids.map(uuid => VideoService.updateVideoReviewStatus(uuid, { approvalStatus: 4 })));
          toast.dismiss(loadingToast);
          ToastNotification({
            message: `${t('general.toast.approve_videos.success')}`,
            iconName: 'success',
            isMobileView: isMobileView,
          });
          loadVideos();
          loadVideosApproved();
        }
      } catch (e) {
        Toast.error(t('general.toast.approve.error'), e);
      }
    },
    [selectedCard]
  );

  const declineVideos = useCallback(
    async (uuid, videoIndex, name) => {
      try {
        const uuids = Object.keys(selectedCard);

        if (uuid) {
          const loadingToast = ToastNotification({
            message: t('general.toast.decline_videos.declining'),
            spinner: true,
            isMobileView: isMobileView,
          });
          await VideoService.updateVideoReviewStatus(uuid, { approvalStatus: 3 });
          toast.dismiss(loadingToast);
          ToastNotification({
            message: `${name} ${t('general.toast.decline.success')}`,
            iconName: 'success',
            isMobileView: isMobileView,
          });
          loadVideos();
          loadVideosApproved();
          return;
        }

        if (uuids.length) {
          const loadingToast = ToastNotification({
            message: t('general.toast.decline_videos.declining'),
            spinner: true,
            isMobileView: isMobileView,
          });
          toggleUnselectCards();
          await Promise.all(uuids.map(uuid => VideoService.updateVideoReviewStatus(uuid, { approvalStatus: 3 })));
          toast.dismiss(loadingToast);
          ToastNotification({
            message: `${t('general.toast.decline_videos.success')}`,
            iconName: 'success',
            isMobileView: isMobileView,
          });
          loadVideos();
          loadVideosApproved();
        }
      } catch (e) {
        Toast.error(t('general.toast.decline.error'), e);
      }
    },
    [selectedCard]
  );

  const revokeVideos = useCallback(
    async (uuid, videoIndex, name) => {
      try {
        const uuids = Object.keys(selectedCard);

        if (uuid) {
          const loadingToast = ToastNotification({
            message: t('general.toast.revoke_videos.revoking'),
            spinner: true,
            isMobileView: isMobileView,
          });
          await VideoService.updateVideoReviewStatus(uuid, { approvalStatus: 3 });
          toast.dismiss(loadingToast);
          ToastNotification({
            message: `${name} ${t('general.toast.revoke.success')}`,
            iconName: 'success',
            isMobileView: isMobileView,
          });
          loadVideos();
          loadVideosApproved();
          return;
        }

        if (uuids.length) {
          const loadingToast = ToastNotification({
            message: t('general.toast.revoke_videos.revoking'),
            spinner: true,
            isMobileView: isMobileView,
          });
          toggleUnselectCards();
          await Promise.all(uuids.map(uuid => VideoService.updateVideoReviewStatus(uuid, { approvalStatus: 3 })));
          toast.dismiss(loadingToast);
          ToastNotification({
            message: `${t('general.toast.revoke_videos.success')}`,
            iconName: 'success',
            isMobileView: isMobileView,
          });
          loadVideos();
          loadVideosApproved();
        }
      } catch (e) {
        Toast.error(t('general.toast.revoke.error'), e);
      }
    },
    [selectedCard]
  );

  const handleCardClick = (event, videoIndex) => {
    const parentElement = event.target.parentElement.parentElement;
    if (
      parentElement.tagName !== 'BUTTON' &&
      parentElement.tagName !== 'LI' &&
      parentElement.tagName !== 'UL' &&
      event.target.tagName !== 'LI' &&
      event.target.tagName !== 'UL'
    ) {
      toggleSelectedCard(videos[videoIndex]);
    }
  };

  const handleDotsClick = id => {
    setShowMenu({
      uuid: id,
      flag: true,
    });
  };

  const handleAllCardClick = () => {
    toggleSelectedAllCard(videos);
  };

  useEffect(() => {
    return () => toggleUnselectCards();
  }, []);

  return (
    <>
      {listMode && videos.length > 0 && (
        <ListHeader isAllCardSelected={Object.keys(selectedCard).length === videos.length} handleAllCardClick={handleAllCardClick} />
      )}
      {videos && (
        <CardsList
          menuRef={menuRef}
          listMode={listMode}
          videos={videos}
          searchInput={searchInput}
          toggleShowMenu={() => setShowMenu({})}
          handleDotsClick={handleDotsClick}
          showMenu={showMenu}
          handleCardClick={handleCardClick}
          approveVideo={selectedFilter.TYPE === approvalStatusesForReviewer.IN_REVIEW.TYPE && approveVideos}
          declineVideo={selectedFilter.TYPE === approvalStatusesForReviewer.IN_REVIEW.TYPE && declineVideos}
          revokeVideo={selectedFilter.TYPE === approvalStatusesForReviewer.APPROVED.TYPE && revokeVideos}
          isMobileView={isMobileView}
        />
      )}
    </>
  );
}

export default ReviewCards;
